import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as THREE from 'three';
import { OBJLoader, mergeBufferGeometries } from 'three-stdlib';

import {
  GlassSelectedVesselSummary,
  QuestionGroup,
  QuestionVM,
} from '../../../core/models/assessment/assessment.model';
import {
  GLASS_REGIONS,
  GlassRegionDefinition,
  GlassRegionSide,
  GlassSegmentKey,
  getGlassStage,
  getGlassTreatmentRecommendation,
} from '../../../core/constants/glass-scoring';
import {
  GLASS_3D_VESSELS,
  Glass3DVesselDefinition,
  Glass3DVesselGroup,
} from '../../../core/constants/glass-3d-vessels';

type GlassViewerMode = '2d' | '3d';
type GlassTargetLimb = 'L' | 'R';

@Component({
  selector: 'app-glass-score-selector',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './glass-score-selector.component.html',
  styleUrl: './glass-score-selector.component.scss',
})
export class GlassScoreSelectorComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('threeViewport') threeViewportRef?: ElementRef<HTMLDivElement>;
  @ViewChild('threeCanvas') threeCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('hoverLabel') hoverLabelRef?: ElementRef<HTMLDivElement>;

  @Input() group: QuestionGroup | null = null;
  @Input() wifiStage: number | null = null;
  @Input() disabledMessage: string | null = null;
  @Output() optionSelected = new EventEmitter<{
    question: QuestionVM;
    optionId: string;
  }>();
  @Output() selectionChanged = new EventEmitter<GlassSelectedVesselSummary[]>();

  readonly regions = GLASS_REGIONS;
  readonly orderedSegments: GlassSegmentKey[] = ['FP', 'IP'];
  readonly orderedVesselGroups: Glass3DVesselGroup[] = [
    'FP',
    'IP',
  ];
  readonly viewerSizes = { width: 560, height: 880 };
  readonly vessels = GLASS_3D_VESSELS;

  viewerMode: GlassViewerMode = '3d';
  isRegionMenuOpen = false;
  showVeins = true;
  showOtherArteries = true;
  selectedRegionIds = new Set<string>();
  targetLimb: GlassTargetLimb | null = null;
  activeSegment: GlassSegmentKey | null = null;
  activeSide: Exclude<GlassRegionSide, 'C'> | null = null;
  interactionNotice: string | null = null;
  rotationX = 0;
  rotationY = 0;
  scale = 1;
  private isDragging = false;
  private dragX = 0;
  private dragY = 0;
  private interactionNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly ngZone = inject(NgZone);
  private readonly translate = inject(TranslateService);
  private readonly threeModelUrl = 'assets/models/glass/FinalBaseMesh.obj';

  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private rootGroup: THREE.Group | null = null;
  private vesselGroup: THREE.Group | null = null;
  private raycaster = new THREE.Raycaster();
  private pointerNdc = new THREE.Vector2();
  private resizeObserver: ResizeObserver | null = null;
  private animationFrame: number | null = null;

  private vesselMeshes: THREE.Mesh[] = [];
  private hitMeshes: THREE.Mesh[] = [];
  private bodyMeshes: THREE.Mesh[] = [];
  private regionMeshMap = new Map<string, THREE.Mesh>();
  private labelAnchorMap = new Map<string, THREE.Vector3>();
  private hoveredRegionId: string | null = null;
  hoveredRegionId2d: string | null = null;

  private activePointerId: number | null = null;
  private pointerDownRegionId: string | null = null;
  private pointerDownTime = 0;
  private pointerPressed = false;
  private pointerDragging = false;
  private pointerDragDistance = 0;
  private pointerLastX = 0;
  private pointerLastY = 0;

  private cameraState = {
    yaw: 0,
    pitch: 0,
    distance: 32,
    minDistance: 12,
    maxDistance: 60,
    target: new THREE.Vector3(0, 0, 0),
  };

  get selectedRegionSummary(): string {
    if (!this.selectedVessels.length) {
      return this.translate.instant('patientCreate.woundAssessment.glass.selectionHint');
    }

    return this.selectedVessels
      .map((region) => this.getVesselDisplayLabel(region.id))
      .join(', ');
  }

  get regionMenuGroups(): Array<{ group: Glass3DVesselGroup; vessels: Glass3DVesselDefinition[] }> {
    return this.orderedVesselGroups.map((group) => ({
      group,
      vessels: this.vessels.filter(
        (vessel) => vessel.group === group && this.isScoreDrivingVessel(vessel),
      ),
    }));
  }

  get selectedVessels(): Glass3DVesselDefinition[] {
    return this.vessels.filter(
      (vessel) => this.selectedRegionIds.has(vessel.id) && this.isScoreDrivingVessel(vessel),
    );
  }

  get selectedVesselSummaries(): GlassSelectedVesselSummary[] {
    return this.selectedVessels
      .filter(
        (vessel): vessel is Glass3DVesselDefinition & { side: 'L' | 'R'; segment: 'FP' | 'IP' } =>
          (vessel.side === 'L' || vessel.side === 'R') &&
          (vessel.segment === 'FP' || vessel.segment === 'IP'),
      )
      .map((vessel) => ({
        id: vessel.id,
        label: this.getVesselDisplayLabel(vessel.id),
        side: vessel.side,
        segment: vessel.segment,
      }));
  }

  private get scoringSideLabel(): string {
    if (!this.targetLimb) {
      return this.translate.instant('patientCreate.woundAssessment.glass.scoringSideUnlocked');
    }

    return this.translate.instant(
      `patientCreate.woundAssessment.glass.targetLimbs.${this.targetLimb}`,
    );
  }

  get hasCompleteStaging(): boolean {
    return this.orderedSegments.every((segment) =>
      !!this.getQuestionByShortCode(this.getShortCodeForSegment(segment))?.answerOptionId,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] && this.group) {
      this.syncActiveSideFromSelections();
      this.syncTargetState();
    }
  }

  ngAfterViewInit(): void {
    this.initThreeViewer();
  }

  ngOnDestroy(): void {
    this.destroyThreeViewer();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (!target.closest('.region-menu-shell')) {
      this.isRegionMenuOpen = false;
    }
  }

  setViewerMode(mode: GlassViewerMode): void {
    if (mode === '2d') {
      return;
    }

    this.viewerMode = mode;
    this.isRegionMenuOpen = false;

    this.initThreeViewer();
    this.syncThreeSelectionState();
    this.handleThreeResize();
  }

  resetView(forceFlat = false): void {
    if (this.viewerMode === '3d' && !forceFlat) {
      this.resetThreeCamera();
      return;
    }

    this.rotationX = 0;
    this.rotationY = 0;
    this.scale = 1;
  }

  setFrontView(): void {
    if (this.viewerMode === '3d') {
      this.setThreeView(0, 0);
      return;
    }

    this.rotationX = 0;
    this.rotationY = 0;
    this.scale = 1;
  }

  setBackView(): void {
    if (this.viewerMode === '3d') {
      this.setThreeView(Math.PI, 0);
      return;
    }

    this.rotationX = 0;
    this.rotationY = 180;
    this.scale = 1;
  }

  onWheel(event: WheelEvent): void {
    if (this.viewerMode !== '2d') {
      return;
    }

    event.preventDefault();
    const nextScale = this.scale - event.deltaY * 0.0014;
    this.scale = Math.max(0.7, Math.min(2.2, nextScale));
  }

  onDragStart(event: MouseEvent): void {
    if (
      this.viewerMode !== '2d' ||
      event.button !== 0 ||
      this.isInteractiveTarget(event.target)
    ) {
      return;
    }

    this.isDragging = true;
    this.dragX = event.clientX;
    this.dragY = event.clientY;
  }

  onDragMove(event: MouseEvent): void {
    if (!this.isDragging || this.viewerMode !== '2d') {
      return;
    }

    const deltaX = event.clientX - this.dragX;
    const deltaY = event.clientY - this.dragY;

    this.rotationY += deltaX * 0.4;
    this.rotationX = Math.max(-55, Math.min(55, this.rotationX - deltaY * 0.35));

    this.dragX = event.clientX;
    this.dragY = event.clientY;
  }

  onDragEnd(): void {
    this.isDragging = false;
  }

  toggleRegion(region: GlassRegionDefinition, event?: Event): boolean {
    const vessel = this.getVesselById(region.id);
    if (!vessel) {
      return false;
    }

    return this.toggleSelectionForVessel(vessel, event);
  }

  toggleVessel(vessel: Glass3DVesselDefinition, event?: Event): boolean {
    return this.toggleSelectionForVessel(vessel, event);
  }

  selectVesselFromMenu(vessel: Glass3DVesselDefinition, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.toggleSelectionForVessel(vessel);

    if (this.selectedRegionIds.has(vessel.id)) {
      const focusPoint = this.getFocusPointForRegion(vessel.id);
      if (focusPoint) {
        this.nudgeTargetTowards(focusPoint, 0.45);
      }
    }
  }

  onRegionHover(region: GlassRegionDefinition): void {
    this.hoveredRegionId2d = region.id;
  }

  onRegionLeave(): void {
    this.hoveredRegionId2d = null;
  }

  isRegionLabelVisible(regionId: string): boolean {
    return this.isRegionSelected(regionId) || this.hoveredRegionId2d === regionId;
  }

  clearSelection(): void {
    this.selectedRegionIds = new Set<string>();
    this.activeSegment = null;
    this.activeSide = null;
    this.targetLimb = null;
    this.syncThreeSelectionState();
    this.emitSelectionState();
  }

  toggleRegionMenu(event?: Event): void {
    event?.stopPropagation();
    this.isRegionMenuOpen = !this.isRegionMenuOpen;
  }

  zoomIn(): void {
    if (this.viewerMode === '3d') {
      this.cameraState.distance = Math.max(
        this.cameraState.minDistance,
        this.cameraState.distance * 0.85,
      );
      this.updateThreeCamera();
      return;
    }

    this.scale = Math.min(2.2, this.scale * 1.1);
  }

  zoomOut(): void {
    if (this.viewerMode === '3d') {
      this.cameraState.distance = Math.min(
        this.cameraState.maxDistance,
        this.cameraState.distance * 1.18,
      );
      this.updateThreeCamera();
      return;
    }

    this.scale = Math.max(0.7, this.scale / 1.1);
  }

  removeRegion(region: Glass3DVesselDefinition | GlassRegionDefinition, event?: Event): void {
    event?.stopPropagation();
    this.removeSelectionById(region.id);
  }

  isRegionSelected(regionId: string): boolean {
    return this.selectedRegionIds.has(regionId);
  }

  get selectedRegions(): GlassRegionDefinition[] {
    return this.regions.filter(
      (region) => this.selectedRegionIds.has(region.id),
    );
  }

  get selectedSegmentSet(): Set<GlassSegmentKey> {
    return new Set(this.selectedRegions.map((region) => region.segment));
  }

  get isDisabled(): boolean {
    return !!this.group?.questions.every((question) => question.isDisabled);
  }

  get viewTransform(): string {
    if (this.viewerMode === '2d') {
      return `scale(${this.scale})`;
    }

    return `translate(-50%, -50%) rotateX(${this.rotationX}deg) rotateY(${this.rotationY}deg) scale(${this.scale})`;
  }

  get glassStage(): number | null {
    return getGlassStage(this.getQuestionScore('GLASS_FP'), this.getQuestionScore('GLASS_IP'));
  }

  get treatmentRecommendation(): string | null {
    return getGlassTreatmentRecommendation(this.glassStage, this.wifiStage);
  }

  hasVisibleSegment(segment: GlassSegmentKey): boolean {
    return this.orderedSegments.includes(segment);
  }

  getRegionsForSegment(segment: GlassSegmentKey): GlassRegionDefinition[] {
    return this.selectedRegions.filter((region) => region.segment === segment);
  }

  getQuestionByShortCode(shortCode: string): QuestionVM | undefined {
    return this.group?.questions.find((question) => question.shortCode === shortCode);
  }

  getOptionLabel(question: QuestionVM | undefined, optionId: string): string {
    const option = question?.options.find((item) => item.id === optionId);

    return option?.value || option?.text || '';
  }

  getOptionBadge(question: QuestionVM | undefined, optionId: string): string {
    const option = question?.options.find((item) => item.id === optionId);
    if (!option) {
      return '';
    }

    const source = option.value || option.text || '';
    const match = source.match(/(\d+)/);
    if (!match) {
      return source;
    }

    return this.translate.instant('patientCreate.woundAssessment.glass.stageBadge', {
      value: match[1],
    });
  }

  getOptionDescription(optionText: string | undefined): string {
    if (!optionText) {
      return '';
    }

    return optionText.replace(/^[A-Z]+\d+\s*-\s*/i, '');
  }

  getQuestionDisplayText(questionText: string | undefined): string {
    return (questionText || '').trim();
  }

  selectOption(question: QuestionVM | undefined, optionId: string): void {
    if (!question || question.isDisabled || this.isDisabled) {
      return;
    }

    this.optionSelected.emit({ question, optionId });
  }

  isSelectedOption(question: QuestionVM | undefined, optionId: string): boolean {
    return question?.answerOptionId === optionId;
  }

  getShortCodeForSegment(segment: GlassSegmentKey): string {
    switch (segment) {
      case 'FP':
        return 'GLASS_FP';
      case 'IP':
        return 'GLASS_IP';
      case 'AI':
        return 'GLASS_AI';
      case 'PE':
        return 'GLASS_PEDAL';
    }
  }

  getVesselGroupLabel(group: Glass3DVesselGroup): string {
    switch (group) {
      case 'AI':
        return this.translate.instant('patientCreate.woundAssessment.glass.sections.AI');
      case 'FP':
        return this.translate.instant('patientCreate.woundAssessment.glass.sections.FP');
      case 'IP':
        return this.translate.instant('patientCreate.woundAssessment.glass.sections.IP');
      case 'PE':
        return this.translate.instant('patientCreate.woundAssessment.glass.sections.PE');
      default:
        return group;
    }
  }

  focusSegment(segment: GlassSegmentKey, event?: Event): void {
    event?.stopPropagation();
    this.activeSegment = segment;
  }

  isSegmentFocused(segment: GlassSegmentKey): boolean {
    return this.activeSegment === segment;
  }

  isSegmentCompleted(segment: GlassSegmentKey): boolean {
    return !!this.getQuestionByShortCode(this.getShortCodeForSegment(segment))?.answerOptionId;
  }

  getSelectedVesselsForSegment(segment: GlassSegmentKey): Glass3DVesselDefinition[] {
    return this.selectedVessels.filter((vessel) => vessel.segment === segment);
  }

  getSegmentContextText(segment: GlassSegmentKey): string {
    const vessels = this.getSelectedVesselsForSegment(segment);
    if (vessels.length) {
      return vessels.map((vessel) => this.getVesselDisplayLabel(vessel.id)).join(', ');
    }

    if (this.targetLimb) {
      return this.translate.instant('patientCreate.woundAssessment.glass.segmentSideActive', {
        side: this.scoringSideLabel,
      });
    }

    return this.translate.instant('patientCreate.woundAssessment.glass.segmentHint');
  }

  getVesselDisplayLabel(vesselId: string): string {
    const glassRegion = this.regions.find((item) => item.id === vesselId);
    if (glassRegion) {
      return this.getRegionDisplayLabel(vesselId);
    }

    const vessel = this.vessels.find((item) => item.id === vesselId);
    if (!vessel) {
      return '';
    }

    const side = vessel.side !== 'C' ? `${vessel.side} ` : '';
    return `${side}${vessel.label}`;
  }

  toggleVeinVisibility(): void {
    this.showVeins = !this.showVeins;
    this.applyVisibilityFilters();
    this.syncThreeSelectionState();
  }

  toggleOtherArteriesVisibility(): void {
    this.showOtherArteries = !this.showOtherArteries;
    this.applyVisibilityFilters();
    this.syncThreeSelectionState();
  }

  private getQuestionScore(shortCode: string): number | null {
    const question = this.getQuestionByShortCode(shortCode);
    const option = question?.options.find((item) => item.id === question.answerOptionId);

    return option?.score ?? null;
  }

  private getVesselById(id: string): Glass3DVesselDefinition | undefined {
    return this.vessels.find((item) => item.id === id);
  }

  private isScoreDrivingVessel(vessel: Glass3DVesselDefinition): boolean {
    return vessel.segment === 'FP' || vessel.segment === 'IP';
  }

  private syncTargetState(): void {
    const selectedSide = this.selectedVessels.find((vessel) => vessel.side !== 'C')?.side;
    this.targetLimb = selectedSide === 'L' || selectedSide === 'R' ? selectedSide : null;
  }

  private ensureTargetLimbForVessel(vessel: Glass3DVesselDefinition): boolean {
    if (vessel.side !== 'L' && vessel.side !== 'R') {
      return true;
    }

    if (this.targetLimb && this.targetLimb !== vessel.side) {
      this.selectedRegionIds = new Set<string>();
      this.activeSegment = null;
      this.activeSide = vessel.side;
      this.targetLimb = vessel.side;
      this.showInteractionNotice(
        this.translate.instant('patientCreate.woundAssessment.glass.sideSwitchNotice', {
          side: this.scoringSideLabel,
          vessel: this.getVesselDisplayLabel(vessel.id),
        }),
      );
      return true;
    }

    if (!this.targetLimb) {
      this.targetLimb = vessel.side;
      this.activeSide = vessel.side;
    }

    return true;
  }

  private toggleSelectionForVessel(vessel: Glass3DVesselDefinition, event?: Event): boolean {
    if (!this.isScoreDrivingVessel(vessel)) {
      event?.stopPropagation();
      this.showInteractionNotice(
        this.translate.instant('patientCreate.woundAssessment.glass.nonScoringVesselNotice', {
          vessel: this.getVesselDisplayLabel(vessel.id),
        }),
      );
      return false;
    }

    this.ensureTargetLimbForVessel(vessel);

    this.activeSegment = vessel.segment;
    this.toggleSelectionById(vessel.id, event);
    return true;
  }

  private showInteractionNotice(message: string): void {
    this.interactionNotice = message;

    if (this.interactionNoticeTimer) {
      clearTimeout(this.interactionNoticeTimer);
    }

    this.interactionNoticeTimer = setTimeout(() => {
      this.interactionNotice = null;
      this.interactionNoticeTimer = null;
    }, 3200);
  }

  private toggleSelectionById(id: string, event?: Event): void {
    event?.stopPropagation();

    if (this.selectedRegionIds.has(id)) {
      this.selectedRegionIds.delete(id);
    } else {
      this.selectedRegionIds.add(id);
    }

    this.syncActiveSideFromSelections();
    this.syncThreeSelectionState();
    this.emitSelectionState();
  }

  private removeSelectionById(id: string): void {
    if (!this.selectedRegionIds.has(id)) {
      return;
    }

    this.selectedRegionIds.delete(id);
    this.syncActiveSideFromSelections();
    this.syncThreeSelectionState();
    this.emitSelectionState();
  }

  private syncActiveSideFromSelections(): void {
    const firstSide = this.selectedRegions.find((region) => region.side !== 'C')?.side;
    this.activeSide = firstSide === 'L' || firstSide === 'R' ? firstSide : null;
  }

  private emitSelectionState(): void {
    this.selectionChanged.emit(this.selectedVesselSummaries);
  }

  private initThreeViewer(): void {
    if (this.renderer || !this.threeViewportRef || !this.threeCanvasRef) {
      return;
    }

    const viewport = this.threeViewportRef.nativeElement;
    const canvas = this.threeCanvasRef.nativeElement;
    const width = Math.max(viewport.clientWidth, 1);
    const height = Math.max(viewport.clientHeight, 1);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 200);
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(width, height, false);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.rootGroup = new THREE.Group();
    this.vesselGroup = new THREE.Group();
    this.rootGroup.add(this.vesselGroup);
    this.scene.add(this.rootGroup);

    this.scene.add(new THREE.HemisphereLight(0xfff4e8, 0x202028, 0.55));

    const keyLight = new THREE.DirectionalLight(0xfff0dc, 1.05);
    keyLight.position.set(8, 18, 14);
    this.scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0xd97c45, 0.12);
    rimLight.position.set(-12, 8, -10);
    this.scene.add(rimLight);

    const fillLight = new THREE.DirectionalLight(0x9ab6ff, 0.3);
    fillLight.position.set(-6, 4, 12);
    this.scene.add(fillLight);

    this.buildThreeVessels();
    this.loadBodyMesh();
    this.fitThreeCameraToScene();
    this.attachThreeListeners();
    this.startRenderLoop();

    this.resizeObserver = new ResizeObserver(() => this.handleThreeResize());
    this.resizeObserver.observe(viewport);
  }

  private attachThreeListeners(): void {
    const viewport = this.threeViewportRef?.nativeElement;
    if (!viewport) {
      return;
    }

    viewport.addEventListener('pointerdown', this.handleThreePointerDown);
    viewport.addEventListener('pointermove', this.handleThreePointerMove);
    viewport.addEventListener('pointerup', this.handleThreePointerUp);
    viewport.addEventListener('pointerleave', this.handleThreePointerLeave);
    viewport.addEventListener('wheel', this.handleThreeWheel, { passive: false });
    this.updateThreeCursor();
  }

  private destroyThreeViewer(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    const viewport = this.threeViewportRef?.nativeElement;
    if (viewport) {
      viewport.removeEventListener('pointerdown', this.handleThreePointerDown);
      viewport.removeEventListener('pointermove', this.handleThreePointerMove);
      viewport.removeEventListener('pointerup', this.handleThreePointerUp);
      viewport.removeEventListener('pointerleave', this.handleThreePointerLeave);
      viewport.removeEventListener('wheel', this.handleThreeWheel);
    }

    if (this.animationFrame != null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (this.rootGroup) {
      this.rootGroup.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    }

    this.vesselMeshes = [];
    this.hitMeshes = [];
    this.bodyMeshes = [];
    this.regionMeshMap.clear();
    this.labelAnchorMap.clear();

    this.renderer?.dispose();
    this.renderer = null;
    this.camera = null;
    this.scene = null;
    this.rootGroup = null;
    this.vesselGroup = null;
  }

  private buildThreeVessels(): void {
    if (!this.vesselGroup) {
      return;
    }

    this.vesselGroup.clear();
    this.vesselMeshes = [];
    this.hitMeshes = [];
    this.bodyMeshes = [];
    this.regionMeshMap.clear();
    this.labelAnchorMap.clear();

    for (const vessel of this.vessels) {
      try {
        const vectors = vessel.points.map((point) => new THREE.Vector3(...point));
        const radius = this.getRadiusForVessel(vessel);

        if (!vectors.length) {
          continue;
        }

        let geometry: THREE.BufferGeometry;
        let glowGeometry: THREE.BufferGeometry;
        let hitGeometry: THREE.BufferGeometry;
        let anchor: THREE.Vector3;

        if (vessel.id === 'heart') {
          const [hx, hy, hz] = [vectors[0].x, vectors[0].y, vectors[0].z];

          const makeHeartGeom = (scale: number): THREE.BufferGeometry => {
            const baseR = 0.42 * scale;

            // Ventricles: sphere tapered at inferior pole into apex
            const vent = new THREE.SphereGeometry(baseR, 28, 22);
            const pos = vent.attributes['position'] as THREE.BufferAttribute;
            for (let i = 0; i < pos.count; i++) {
              const y = pos.getY(i);
              if (y < 0) {
                const t = 1 + (y / baseR) * 0.55;
                pos.setX(i, pos.getX(i) * t);
                pos.setZ(i, pos.getZ(i) * t * 0.85);
              }
            }
            vent.computeVertexNormals();
            vent.scale(0.95, 1.30, 0.90);
            vent.rotateZ(0.32);
            vent.rotateX(0.10);

            // Right atrium
            const ra = new THREE.SphereGeometry(0.26 * scale, 20, 16);
            ra.scale(1.0, 0.85, 0.90);
            ra.translate(-0.30 * scale, 0.35 * scale, -0.10 * scale);

            // Left atrium
            const la = new THREE.SphereGeometry(0.22 * scale, 20, 16);
            la.scale(1.0, 0.85, 0.85);
            la.translate(0.18 * scale, 0.40 * scale, -0.28 * scale);

            const merged = mergeBufferGeometries([vent, ra, la], false)!;
            merged.translate(hx, hy, hz);
            return merged;
          };

          geometry = makeHeartGeom(1.0);
          glowGeometry = makeHeartGeom(1.28);
          hitGeometry = makeHeartGeom(1.5);
          anchor = new THREE.Vector3(hx, hy + 0.10, hz);
        } else if (vectors.length === 1) {
          const center = vectors[0];
          geometry = new THREE.SphereGeometry(radius * 3.4, 20, 18);
          geometry.translate(center.x, center.y, center.z);

          glowGeometry = new THREE.SphereGeometry(radius * 5.4, 20, 18);
          glowGeometry.translate(center.x, center.y, center.z);

          hitGeometry = new THREE.SphereGeometry(radius * 8, 16, 14);
          hitGeometry.translate(center.x, center.y, center.z);

          anchor = center.clone();
        } else {
          const curve = new THREE.CatmullRomCurve3(vectors, false, 'catmullrom', 0.5);
          const tubularSegments = Math.max(24, vectors.length * 14);

          geometry = new THREE.TubeGeometry(curve, tubularSegments, radius, 14, false);
          glowGeometry = new THREE.TubeGeometry(
            curve,
            tubularSegments,
            radius * 1.8,
            14,
            false,
          );
          hitGeometry = new THREE.TubeGeometry(
            curve,
            tubularSegments,
            Math.max(radius * 5.2, 0.28),
            10,
            false,
          );

          anchor = curve.getPoint(0.5);
        }

        const isHeart = vessel.id === 'heart';
        const baseColor = isHeart ? 0xc0392b : vessel.kind === 'vein' ? 0x7a9dd2 : 0xf0c39a;
        const baseEmissive = isHeart ? 0x7b1a12 : vessel.kind === 'vein' ? 0x1d3760 : 0x5b3420;
        const glowColor = isHeart ? 0xff2020 : vessel.kind === 'vein' ? 0x3d74c4 : 0xff7900;

        const vesselMaterial = new THREE.MeshStandardMaterial({
          color: baseColor,
          roughness: 0.35,
          metalness: 0.1,
          emissive: baseEmissive,
          emissiveIntensity: 0.5,
        });
        const glowMaterial = new THREE.MeshBasicMaterial({
          color: glowColor,
          transparent: true,
          opacity: 0,
          depthWrite: false,
        });
        const hitMaterial = new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0,
          depthWrite: false,
          side: THREE.DoubleSide,
        });

        const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
        glowMesh.renderOrder = 9;

        const vesselMesh = new THREE.Mesh(geometry, vesselMaterial);
        vesselMesh.renderOrder = 10;
        vesselMesh.userData['regionId'] = vessel.id;
        vesselMesh.userData['glowMesh'] = glowMesh;
        vesselMesh.userData['kind'] = vessel.kind;
        vesselMesh.userData['group'] = vessel.group;

        const hitMesh = new THREE.Mesh(hitGeometry, hitMaterial);
        hitMesh.userData['regionId'] = vessel.id;
        hitMesh.userData['kind'] = vessel.kind;
        hitMesh.userData['group'] = vessel.group;

        this.vesselGroup.add(glowMesh);
        this.vesselGroup.add(vesselMesh);
        this.vesselGroup.add(hitMesh);

        this.vesselMeshes.push(vesselMesh);
        this.hitMeshes.push(hitMesh);
        this.regionMeshMap.set(vessel.id, vesselMesh);
        this.labelAnchorMap.set(vessel.id, anchor);
      } catch (error) {
        console.error('Failed to build vessel geometry', vessel.id, error);
      }
    }

    this.applyVisibilityFilters();
    this.syncThreeSelectionState();
  }

  private loadBodyMesh(): void {
    if (!this.rootGroup) {
      return;
    }

    const loader = new OBJLoader();
    loader.load(
      this.threeModelUrl,
      (object) => {
        object.traverse((child) => {
          if (!(child instanceof THREE.Mesh)) {
            return;
          }

          child.geometry.computeVertexNormals();
          child.material = new THREE.MeshPhysicalMaterial({
            color: 0xc3a58d,
            roughness: 0.55,
            metalness: 0,
            transparent: true,
            opacity: 0.24,
            depthWrite: false,
            side: THREE.DoubleSide,
            clearcoat: 0.15,
            clearcoatRoughness: 0.7,
          });
          child.renderOrder = 5;

          const shell = new THREE.Mesh(
            child.geometry,
            new THREE.MeshBasicMaterial({
              color: 0x3a2a20,
              transparent: true,
              opacity: 0.05,
              side: THREE.BackSide,
              depthWrite: false,
            }),
          );
          shell.renderOrder = 4;
          child.parent?.add(shell);
          this.bodyMeshes.push(child);
        });

        this.rootGroup?.add(object);
        this.fitThreeCameraToScene();
      },
      undefined,
      (error) => {
        console.error('Failed to load GLASS body mesh', error);
      },
    );
  }

  private fitThreeCameraToScene(): void {
    if (!this.rootGroup || !this.camera) {
      return;
    }

    this.rootGroup.position.set(0, 0, 0);
    const box = new THREE.Box3().setFromObject(this.rootGroup);
    if (box.isEmpty()) {
      return;
    }

    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    this.rootGroup.position.set(-center.x, -center.y, -center.z);

    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * this.camera.aspect);
    const distanceY = size.y / (2 * Math.tan(verticalFov / 2));
    const distanceX = size.x / (2 * Math.tan(horizontalFov / 2));
    const distanceZ = size.z * 2.4;
    const distance = Math.max(distanceX, distanceY, distanceZ, 18) * 1.15;

    this.cameraState = {
      yaw: 0,
      pitch: 0,
      distance,
      minDistance: Math.max(10, distance * 0.45),
      maxDistance: distance * 3.2,
      target: new THREE.Vector3(0, 0, 0),
    };

    this.updateThreeCamera();
  }

  private updateThreeCamera(): void {
    if (!this.camera) {
      return;
    }

    const { yaw, pitch, distance } = this.cameraState;
    const cosPitch = Math.cos(pitch);
    const sinPitch = Math.sin(pitch);
    const cosYaw = Math.cos(yaw);
    const sinYaw = Math.sin(yaw);

    const x = distance * cosPitch * sinYaw;
    const y = distance * sinPitch;
    const z = distance * cosPitch * cosYaw;

    this.camera.position.set(
      this.cameraState.target.x + x,
      this.cameraState.target.y + y,
      this.cameraState.target.z + z,
    );
    this.camera.lookAt(this.cameraState.target);
  }

  private resetThreeCamera(): void {
    this.cameraState.yaw = 0;
    this.cameraState.pitch = 0;
    this.cameraState.target.set(0, 0, 0);
    this.updateThreeCamera();
    this.updateHoverLabel();
  }

  private setThreeView(yaw: number, pitch: number): void {
    this.cameraState.yaw = yaw;
    this.cameraState.pitch = pitch;
    this.updateThreeCamera();
    this.updateHoverLabel();
  }

  private handleThreeResize(): void {
    const viewport = this.threeViewportRef?.nativeElement;
    if (!viewport || !this.renderer || !this.camera) {
      return;
    }

    const width = Math.max(viewport.clientWidth, 1);
    const height = Math.max(viewport.clientHeight, 1);

    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.fitThreeCameraToScene();
  }

  private startRenderLoop(): void {
    const tick = () => {
      if (!this.scene || !this.camera || !this.renderer) {
        return;
      }

      const pulse = performance.now() * 0.004;
      for (const mesh of this.vesselMeshes) {
        const regionId = mesh.userData['regionId'] as string;
        const selected = this.selectedRegionIds.has(regionId);
        const glowMesh = mesh.userData['glowMesh'] as THREE.Mesh | undefined;
        const material = mesh.material as THREE.MeshStandardMaterial;
        const glowMaterial = glowMesh?.material as THREE.MeshBasicMaterial | undefined;

        if (selected) {
          const amount = 0.5 + 0.5 * Math.sin(pulse);
          material.emissiveIntensity = 0.95 + 0.25 * amount;
          if (glowMaterial) {
            glowMaterial.opacity = 0.18 + 0.18 * amount;
          }
        } else {
          const isHovered = this.hoveredRegionId === regionId;
          material.emissiveIntensity = isHovered ? 0.55 : 0.42;
          if (glowMaterial) {
            glowMaterial.opacity = isHovered ? 0.08 : 0;
          }
        }
      }

      this.updateHoverLabel();
      this.renderer.render(this.scene, this.camera);
      this.animationFrame = requestAnimationFrame(tick);
    };

    tick();
  }

  private updateHoverLabel(): void {
    if (!this.camera || !this.rootGroup || !this.hoverLabelRef || !this.threeCanvasRef) {
      return;
    }

    const label = this.hoverLabelRef.nativeElement;
    const regionId =
      this.hoveredRegionId ??
      (this.selectedVessels.length === 1 ? this.selectedVessels[0].id : null);

    if (!regionId) {
      label.classList.remove('show');
      return;
    }

    const anchor = this.labelAnchorMap.get(regionId);
    if (!anchor) {
      label.classList.remove('show');
      return;
    }

    const projected = this.rootGroup.localToWorld(anchor.clone()).project(this.camera);
    if (projected.z > 1) {
      label.classList.remove('show');
      return;
    }

    const canvas = this.threeCanvasRef.nativeElement;
    const x = (projected.x * 0.5 + 0.5) * canvas.clientWidth;
    const y = (-projected.y * 0.5 + 0.5) * canvas.clientHeight;

    label.style.transform = `translate(${x}px, ${y}px) translate(-50%, -130%)`;
    label.textContent = this.getVesselDisplayLabel(regionId);
    label.classList.add('show');
  }

  private syncThreeSelectionState(): void {
    for (const mesh of this.vesselMeshes) {
      const regionId = mesh.userData['regionId'] as string;
      const kind = mesh.userData['kind'] as string;
      const glowMesh = mesh.userData['glowMesh'] as THREE.Mesh | undefined;
      const material = mesh.material as THREE.MeshStandardMaterial;
      const glowMaterial = glowMesh?.material as THREE.MeshBasicMaterial | undefined;
      const isSelected = this.selectedRegionIds.has(regionId);
      const isHovered = this.hoveredRegionId === regionId;

      const isVein = kind === 'vein';
      const isHeart = regionId === 'heart';
      const baseColor = isHeart ? 0xc0392b : isVein ? 0x6e92c8 : 0xe8b789;
      const baseEmissive = isHeart ? 0x7b1a12 : isVein ? 0x162a4a : 0x4a2a1a;
      const hoverColor = isHeart ? 0xe05545 : isVein ? 0x8fb4e8 : 0xf3d8b6;
      const hoverEmissive = isHeart ? 0x9b2a20 : isVein ? 0x1e3a62 : 0x6a3a18;

      const selectedColor = isHeart ? 0xe05545 : isVein ? 0x5ba3f5 : 0xff8030;
      const selectedEmissive = isHeart ? 0xc0392b : isVein ? 0x1a4a8a : 0xff6600;
      const selectedGlowOpacity = isHeart ? 0.28 : isVein ? 0.22 : 0.3;

      if (isSelected) {
        material.color.set(selectedColor);
        material.emissive.set(selectedEmissive);
        material.emissiveIntensity = 1.25;
        if (glowMaterial) {
          glowMaterial.opacity = selectedGlowOpacity;
        }
      } else if (isHovered) {
        material.color.set(hoverColor);
        material.emissive.set(hoverEmissive);
        material.emissiveIntensity = 0.55;
        if (glowMaterial) {
          glowMaterial.opacity = 0.08;
        }
      } else {
        material.color.set(baseColor);
        material.emissive.set(baseEmissive);
        material.emissiveIntensity = 0.42;
        if (glowMaterial) {
          glowMaterial.opacity = 0;
        }
      }
    }
  }

  private getRadiusForVessel(vessel: Glass3DVesselDefinition): number {
    const key = vessel.group;
    switch (key) {
      case 'AI':
        return 0.18;
      case 'FP':
        return 0.14;
      case 'IP':
        return 0.1;
      case 'PE':
        return 0.08;
      case 'TX':
      case 'AB':
      case 'VEN_AB':
        return 0.08;
      case 'VEN_LE':
        return 0.065;
      case 'HN':
      case 'UA':
      default:
        return 0.055;
    }
  }

  private getRegionDisplayLabel(regionId: string): string {
    const region = this.regions.find((item) => item.id === regionId);
    if (!region) {
      return '';
    }

    const side = region.side !== 'C' ? `${region.side} ` : '';
    return `${side}${this.translate.instant(
      `patientCreate.woundAssessment.glass.regions.${region.labelKey}`,
    )}`;
  }

  private isVesselVisible(vessel: Glass3DVesselDefinition): boolean {
    const isPadGroup = vessel.group === 'AI' || vessel.group === 'FP' || vessel.group === 'IP' || vessel.group === 'PE';

    if (vessel.kind === 'vein') {
      return this.showVeins;
    }

    if (!isPadGroup) {
      return this.showOtherArteries;
    }

    return true;
  }

  private applyVisibilityFilters(): void {
    for (const vessel of this.vessels) {
      const visible = this.isVesselVisible(vessel);
      const mesh = this.regionMeshMap.get(vessel.id);
      const hitMesh = this.hitMeshes.find((item) => item.userData['regionId'] === vessel.id);
      const glowMesh = mesh?.userData['glowMesh'] as THREE.Mesh | undefined;

      if (mesh) {
        mesh.visible = visible;
      }
      if (hitMesh) {
        hitMesh.visible = visible;
      }
      if (glowMesh) {
        glowMesh.visible = visible;
      }

      if (!visible) {
        this.selectedRegionIds.delete(vessel.id);
      }
    }

    this.syncActiveSideFromSelections();
    this.emitSelectionState();
  }

  private updateThreeCursor(): void {
    const viewport = this.threeViewportRef?.nativeElement;
    if (!viewport) {
      return;
    }

    if (this.pointerDragging) {
      viewport.style.cursor = 'grabbing';
      return;
    }

    if (this.pointerPressed) {
      viewport.style.cursor = this.hoveredRegionId ? 'pointer' : 'grab';
      return;
    }

    viewport.style.cursor = this.hoveredRegionId ? 'pointer' : 'grab';
  }

  private updatePointerFromEvent(event: MouseEvent | PointerEvent | WheelEvent): void {
    const viewport = this.threeViewportRef?.nativeElement;
    if (!viewport) {
      return;
    }

    const rect = viewport.getBoundingClientRect();
    this.pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private pickRegionId(): string | null {
    if (!this.camera || !this.scene) {
      return null;
    }

    this.scene.updateMatrixWorld(false);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const visibleHitMeshes = this.hitMeshes.filter((m) => m.visible);
    const hits = this.raycaster.intersectObjects(visibleHitMeshes, false);
    return (hits[0]?.object.userData['regionId'] as string | undefined) ?? null;
  }

  private getPointerWorldPoint(): THREE.Vector3 | null {
    if (!this.camera || !this.scene) {
      return null;
    }

    this.scene.updateMatrixWorld(false);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);

    const visibleTargets = [
      ...this.hitMeshes.filter((mesh) => mesh.visible),
      ...this.bodyMeshes.filter((mesh) => mesh.visible),
    ];
    const hits = this.raycaster.intersectObjects(visibleTargets, false);
    if (hits[0]?.point) {
      return hits[0].point.clone();
    }

    return this.getPointerPlanePoint();
  }

  private getPointerPlanePoint(): THREE.Vector3 | null {
    if (!this.camera) {
      return null;
    }

    const cameraDirection = new THREE.Vector3();
    this.camera.getWorldDirection(cameraDirection);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      cameraDirection,
      this.cameraState.target,
    );
    const point = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(plane, point) ? point : null;
  }

  private getFocusPointForRegion(regionId: string | null): THREE.Vector3 | null {
    if (!regionId || !this.rootGroup) {
      return null;
    }

    const anchor = this.labelAnchorMap.get(regionId);
    if (!anchor) {
      return null;
    }

    return this.rootGroup.localToWorld(anchor.clone());
  }

  private nudgeTargetTowards(point: THREE.Vector3, amount: number): void {
    this.cameraState.target.lerp(point, amount);
    this.updateThreeCamera();
  }

  private panThreeCamera(deltaX: number, deltaY: number): void {
    if (!this.camera || !this.threeViewportRef) {
      return;
    }

    const viewport = this.threeViewportRef.nativeElement;
    const distance = this.camera.position.distanceTo(this.cameraState.target);
    const verticalSpan = 2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) * distance;
    const horizontalSpan = verticalSpan * this.camera.aspect;
    const worldPerPixelX = horizontalSpan / Math.max(viewport.clientWidth, 1);
    const worldPerPixelY = verticalSpan / Math.max(viewport.clientHeight, 1);

    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    this.camera.matrixWorld.extractBasis(right, up, new THREE.Vector3());

    const offset = right
      .multiplyScalar(-deltaX * worldPerPixelX)
      .add(up.multiplyScalar(deltaY * worldPerPixelY));

    this.cameraState.target.add(offset);
    this.updateThreeCamera();
  }

  private readonly handleThreePointerDown = (event: PointerEvent): void => {
    if (this.viewerMode !== '3d' || event.button !== 0) {
      return;
    }

    this.activePointerId = event.pointerId;
    this.pointerPressed = true;
    this.pointerDragging = false;
    this.pointerDragDistance = 0;
    this.pointerDownTime = performance.now();
    this.pointerLastX = event.clientX;
    this.pointerLastY = event.clientY;
    this.updatePointerFromEvent(event);
    this.pointerDownRegionId = this.pickRegionId();

    try {
      this.threeViewportRef?.nativeElement.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }

    this.updateThreeCursor();
  };

  private readonly handleThreePointerMove = (event: PointerEvent): void => {
    if (this.viewerMode !== '3d') {
      return;
    }

    if (!this.pointerPressed || event.pointerId !== this.activePointerId) {
      // Hover update — only when not actively dragging
      this.updatePointerFromEvent(event);
      const hoveredRegionId = this.pickRegionId();
      if (hoveredRegionId !== this.hoveredRegionId) {
        this.hoveredRegionId = hoveredRegionId;
        this.syncThreeSelectionState();
        this.updateThreeCursor();
      }
      return;
    }

    const deltaX = event.clientX - this.pointerLastX;
    const deltaY = event.clientY - this.pointerLastY;
    this.pointerDragDistance += Math.abs(deltaX) + Math.abs(deltaY);
    this.pointerLastX = event.clientX;
    this.pointerLastY = event.clientY;

    if (!this.pointerDragging && this.pointerDragDistance > 12) {
      this.pointerDragging = true;
      this.updateThreeCursor();
    }

    if (!this.pointerDragging) {
      return;
    }

    if (event.shiftKey) {
      this.panThreeCamera(deltaX, deltaY);
      return;
    }

    this.cameraState.yaw -= deltaX * 0.0065;
    this.cameraState.pitch = THREE.MathUtils.clamp(
      this.cameraState.pitch + deltaY * 0.0065,
      -0.95,
      0.95,
    );
    this.updateThreeCamera();
  };

  private readonly handleThreePointerUp = (event: PointerEvent): void => {
    if (this.viewerMode !== '3d' || event.pointerId !== this.activePointerId) {
      return;
    }

    // Treat as click if distance < 12px AND held < 600ms
    const elapsed = performance.now() - this.pointerDownTime;
    const isClick = !this.pointerDragging && this.pointerDragDistance < 12 && elapsed < 600;

    if (isClick) {
      // Use the region captured at pointerdown — more reliable than re-picking at pointerup
      const regionId = this.pointerDownRegionId;
      if (regionId) {
        const vessel = this.vessels.find((item) => item.id === regionId);
        if (vessel) {
          let selectionApplied = false;
          this.ngZone.run(() => {
            selectionApplied = this.toggleVessel(vessel);
          });
          if (selectionApplied) {
            const focusPoint = this.getFocusPointForRegion(regionId);
            if (focusPoint) {
              this.nudgeTargetTowards(focusPoint, 0.45);
            }
          }
        }
      }
    }

    this.pointerPressed = false;
    this.pointerDragging = false;
    this.pointerDragDistance = 0;
    this.activePointerId = null;
    this.pointerDownRegionId = null;

    try {
      this.threeViewportRef?.nativeElement.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }

    // Restore hover state after click
    this.updatePointerFromEvent(event);
    this.hoveredRegionId = this.pickRegionId();
    this.syncThreeSelectionState();
    this.updateThreeCursor();
  };

  private readonly handleThreePointerLeave = (): void => {
    if (this.pointerPressed) {
      return;
    }

    this.hoveredRegionId = null;
    this.syncThreeSelectionState();
    this.updateThreeCursor();
  };

  private readonly handleThreeWheel = (event: WheelEvent): void => {
    if (this.viewerMode !== '3d') {
      return;
    }

    event.preventDefault();
    this.updatePointerFromEvent(event);
    const focusPoint =
      this.getPointerWorldPoint() ??
      this.getFocusPointForRegion(this.hoveredRegionId) ??
      this.getFocusPointForRegion(this.selectedVessels[0]?.id ?? null);
    const previousDistance = this.cameraState.distance;
    const nextDistance = this.cameraState.distance * (1 + event.deltaY * 0.0012);
    this.cameraState.distance = THREE.MathUtils.clamp(
      nextDistance,
      this.cameraState.minDistance,
      this.cameraState.maxDistance,
    );

    if (focusPoint && previousDistance !== this.cameraState.distance) {
      const zoomRatio = (previousDistance - this.cameraState.distance) / previousDistance;
      const focusOffset = focusPoint.clone().sub(this.cameraState.target);
      this.cameraState.target.add(focusOffset.multiplyScalar(zoomRatio));
    }

    this.updateThreeCamera();
  };

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    return !!target.closest(
      '.viewer-toolbar, .viewer-mode-switch, .region-label, .region-chip, .clear-button, .glass-form-check-list, .form-check-label, .form-check-input, .vessel-hit, .vessel, .scene-actions, .region-menu-shell, .selection-overlay',
    );
  }
}
